/**
 * Traducción de errores de la API de Locaciones a la vista (ADR 0021).
 *
 * El backend responde con un contrato uniforme; `apiClient` ya lo normaliza a `ApiError`.
 * Acá se decide qué mostrar y a qué campo del formulario apuntar el error:
 * - Siempre se muestra el `message` del backend (fuente de verdad del texto).
 * - Si el error identifica el campo (`details.field` / `details.fieldName`), se marca ese.
 * - Si no, se cae al campo conocido por el `code` estable (ADR 0021: sin parsear mensajes).
 *
 * `LOCACION_DATOS_INVALIDOS` (falta un campo obligatorio / coordenadas) no apunta a un campo
 * único: se muestra como mensaje. `LOCACION_NOT_FOUND` (404) lo resuelve el hook de detalle.
 */
import { ApiError } from '@/lib/http/apiClient';

export interface LocationApiError {
  /** Mensaje del backend, listo para mostrar. */
  message: string;
  /** Nombre del campo del formulario a marcar, si el error lo identifica. */
  field?: string;
}

/**
 * Códigos estables → campo del formulario (nombres del contrato). Coordenadas, aliado y
 * el nombre duplicado (TG-123) son los errores que el backend asocia a un campo concreto.
 */
const CODE_FIELD: Record<string, string> = {
  LOCACION_COORDENADAS_INVALIDAS: 'coordinates',
  LOCACION_ALIADO_INVALID: 'allyId',
  LOCACION_NOMBRE_DUPLICADO: 'name',
};

/**
 * Redacción de los duplicados de unicidad, espejo del backend (TG-123). La reusa el
 * chequeo consultivo por paso, que no recibe el `message` del POST (solo el booleano)
 * pero debe decir lo mismo para no inventar otro texto.
 */
export const LOCATION_DUPLICATE_MESSAGES = {
  name: 'Ya existe una ubicación con ese nombre',
};

/** Extrae el campo de `details` (convención ADR 0021) si viene. */
function fieldFromDetails(details: unknown): string | undefined {
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  const record = details as Record<string, unknown>;
  for (const key of ['field', 'fieldName'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return undefined;
}

/**
 * Describe un error de locación para mostrarlo en la página. `fallbackMessage` ajusta el
 * texto genérico según la acción (alta, edición, sync).
 */
export function describeLocationApiError(
  error: unknown,
  fallbackMessage = 'No se pudo crear la ubicación.',
): LocationApiError {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      field: fieldFromDetails(error.details) ?? CODE_FIELD[error.code],
    };
  }
  if (error instanceof Error && error.message) {
    return { message: error.message };
  }
  return { message: fallbackMessage };
}
